import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const InsightsPage = () => {
  const insights = [
    {
      category: 'Healthcare',
      title: 'Digital Transformation in Healthcare: A Strategic Imperative',
      excerpt: 'Healthcare organizations must embrace digital transformation to improve patient outcomes, operational efficiency, and competitive positioning in an increasingly technology-driven landscape.',
      date: 'March 15, 2025',
      readTime: '8 min read',
      image: 'https://public.readdy.ai/ai/img_res/a81f1b4d7bbd2ee7a64ce24a5a338ead.jpg',
    },
    {
      category: 'Logistics',
      title: 'Optimizing Fleet Operations: Technology and Compliance',
      excerpt: 'Transportation companies face dual challenges of regulatory compliance and operational efficiency. Strategic technology adoption and process optimization deliver measurable improvements.',
      date: 'March 10, 2025',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=modern%20fleet%20management%20technology%20with%20GPS%20tracking%20and%20route%20optimization%20displays%20in%20professional%20logistics%20operations%20center%2C%20transportation%20technology%20concept%20with%20sophisticated%20design&width=600&height=400&seq=insight-logistics-002&orientation=landscape',
    },
    {
      category: 'Technology',
      title: 'Cybersecurity in the Modern Enterprise: Beyond Compliance',
      excerpt: 'Effective cybersecurity requires more than regulatory compliance. Organizations must adopt comprehensive security frameworks that protect assets while enabling business operations.',
      date: 'March 5, 2025',
      readTime: '7 min read',
      image: 'https://readdy.ai/api/search-image?query=cybersecurity%20operations%20center%20with%20multiple%20screens%20showing%20security%20monitoring%20and%20threat%20detection%20systems%2C%20professional%20IT%20security%20environment%20with%20dark%20sophisticated%20atmosphere&width=600&height=400&seq=insight-tech-003&orientation=landscape',
    },
    {
      category: 'Fuel Retail',
      title: 'Maximizing Profitability in Fuel Retail: Strategic Approaches',
      excerpt: 'Gas station owners and convenience store operators can significantly improve profitability through strategic pricing, merchandising optimization, and operational efficiency improvements.',
      date: 'February 28, 2025',
      readTime: '5 min read',
      image: 'https://readdy.ai/api/search-image?query=abstract%20business%20growth%20chart%20with%20rising%20profit%20arrows%20and%20financial%20analytics%20visualization%20on%20dark%20background%2C%20strategic%20profitability%20concept%20with%20golden%20amber%20and%20copper%20metallic%20tones%20representing%20fuel%20and%20energy%20sector%20success%2C%20premium%20corporate%20artwork%20without%20any%20logos%20or%20brand%20elements&width=600&height=400&seq=insight-fuel-new-013&orientation=landscape',
    },
    {
      category: 'Finance',
      title: 'Capital Planning for Growth: A Strategic Framework',
      excerpt: 'Successful capital planning requires comprehensive financial analysis, strategic thinking, and compelling narratives that resonate with lenders and investors.',
      date: 'February 20, 2025',
      readTime: '9 min read',
      image: 'https://readdy.ai/api/search-image?query=professional%20financial%20planning%20meeting%20with%20business%20executives%20reviewing%20investment%20charts%20and%20capital%20strategy%20documents%20in%20modern%20corporate%20boardroom%2C%20finance%20advisory%20concept%20with%20sophisticated%20atmosphere&width=600&height=400&seq=insight-finance-005&orientation=landscape',
    },
    {
      category: 'Business Strategy',
      title: 'Operational Excellence: Principles and Practices',
      excerpt: 'Achieving operational excellence requires disciplined execution, continuous improvement, and alignment of people, processes, and technology toward strategic objectives.',
      date: 'February 15, 2025',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=business%20operations%20team%20analyzing%20performance%20metrics%20and%20process%20improvements%20in%20modern%20corporate%20office%2C%20operational%20excellence%20concept%20with%20professional%20environment%20and%20clean%20design&width=600&height=400&seq=insight-strategy-006&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      <main className="pt-[90px]">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-[#0A0A0A] to-black py-32">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 border border-copper/30 rounded-full mb-8">
                <span className="text-xs uppercase tracking-wider text-copper">Insights & Perspectives</span>
              </div>
              <h1 className="text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
                Executive Insights &{' '}
                <span className="bg-copper-gradient bg-clip-text text-transparent">
                  Industry Perspectives
                </span>
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed">
                Strategic thinking and practical guidance from MACGOC's leadership team on critical business challenges across technology, healthcare, logistics, fuel retail, and finance.
              </p>
            </div>
          </div>
        </section>

        {/* Insights Grid */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {insights.map((insight, index) => (
                <article
                  key={index}
                  className="group bg-charcoal border border-charcoal-light rounded-2xl overflow-hidden hover:border-copper transition-all duration-300 cursor-pointer"
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={insight.image}
                      alt={insight.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-copper text-white text-xs font-semibold rounded-full uppercase tracking-wide">
                        {insight.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <h2 className="text-2xl font-bold text-white mb-4 leading-tight group-hover:text-copper transition-colors">
                      {insight.title}
                    </h2>
                    <p className="text-base text-gray-400 leading-relaxed mb-6">
                      {insight.excerpt}
                    </p>
                    
                    {/* Meta */}
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{insight.date}</span>
                      <span>{insight.readTime}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="bg-black py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="bg-charcoal border border-charcoal-light rounded-3xl p-16 text-center">
              <h2 className="text-4xl font-bold text-white mb-6">
                Subscribe to Executive Insights
              </h2>
              <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
                Receive monthly insights and strategic perspectives from MACGOC's leadership team directly to your inbox.
              </p>
              <form className="max-w-md mx-auto relative" onSubmit={(e) => e.preventDefault()}>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="w-full bg-charcoal-dark border border-charcoal-light rounded-full h-14 px-6 pr-32 text-base text-white placeholder-gray-600 focus:outline-none focus:border-copper transition-colors"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-2 bg-copper hover:bg-copper-light text-white px-8 py-2.5 rounded-full font-semibold transition-colors whitespace-nowrap"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8 text-center">
            <h2 className="text-5xl font-bold text-white mb-6">
              Ready to Discuss Your Challenges?
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Schedule a consultation to explore how MACGOC can help transform your operations.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-copper hover:bg-copper-light text-white px-12 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap"
            >
              Get in Touch
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default InsightsPage;